import { useEffect, useMemo } from "react";
import { useNode } from "@craftjs/core";
import { useFormikContext } from "formik";
import DateInputSettings from "../settings/DateInputSettings";
import useResponsiveValues from "@/components/editor/hooks/useResponsiveValues";
import { CssClass } from "@/components/editor/settings/ClassSettings";
import slugify from "slugify";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { InputAdornment } from "@mui/material";
import DynamicHugeIcon from "@/components/HugeIcon/DynamicHugeIcon";
import { format } from "date-fns";

interface Availability {
  weekDays: number[];
  blockedDates: string[];
}

interface DateInputProps extends CssClass {
  label: string;
  value: string | null;
  variant?: "outlined" | "filled" | "standard";
  fullWidth?: boolean;
  size?: "small" | "medium";
  helperText?: string;
  disablePast?: boolean;
  required?: boolean;
  icon?: string;
  iconLocation?: "start" | "end";
  iconStroke?: number;
  iconSize?: number;
  iconColor?: string;
  availability?: Availability;
}

const DateInputBlock = ({
  label,
  value,
  variant,
  fullWidth,
  size,
  helperText,
  disablePast,
  required,
  icon,
  iconLocation,
  iconStroke,
  iconSize,
  iconColor,
  availability,
}: Partial<DateInputProps>) => {
  const responsive = useResponsiveValues();
  const formik = useFormikContext<any>();
  const fieldId = useMemo(() => slugify(label ?? "", { lower: true }), [label]);

  useEffect(() => {
    if (formik && value) {
      formik.setFieldValue(fieldId, value);
    }
  }, [fieldId]);

  const isFormik = !!formik;
  const fieldValue = isFormik ? formik.values[fieldId] : value;

  const handleChange = (newValue: any) => {
    if (isFormik) {
      formik.setFieldValue(fieldId, newValue);
    }
  };

  const {
    id,
    connectors: { connect, drag },
  } = useNode((state) => {
    return {
      hasSelectedNode: state.events.selected,
      hasDraggedNode: state.events.dragged,
    };
  });

  const resolvedColor = useMemo(() => responsive.getValue(iconColor), [iconColor, responsive]);

  const adornment = icon ? (
    <InputAdornment position={iconLocation ?? "start"}>
      <DynamicHugeIcon name={icon} size={iconSize ?? 20} strokeWidth={iconStroke ?? 2} color={resolvedColor} />
    </InputAdornment>
  ) : null;

  const shouldDisableDate = (date: Date) => {
    const weekDays = availability?.weekDays ?? [];
    const blockedDates = availability?.blockedDates ?? [];

    // Solo permitir los días seleccionados
    if (weekDays.length > 0 && !weekDays.includes(date.getDay())) {
      return true;
    }

    // Bloquear fechas específicas
    if (blockedDates.includes(format(date, "yyyy-MM-dd"))) {
      return true;
    }

    return false;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div ref={(ref: any) => connect(drag(ref))}>
        {/* get the date picker with this format day, month, year */}
        <DatePicker
          key={`${id}-${responsive.viewport}`}
          label={label}
          value={fieldValue ? new Date(fieldValue) : null}
          onChange={handleChange}
          disablePast={disablePast}
          shouldDisableDate={shouldDisableDate}
          format={"dd/MM/yyyy"}
          slotProps={{
            textField: {
              readOnly: true,
              variant: variant,
              fullWidth: fullWidth,
              size: size,
              helperText: helperText,
              name: fieldId,
              required: required,
              InputProps: {
                startAdornment: iconLocation === "start" ? adornment : undefined,
                endAdornment: iconLocation === "end" ? adornment : undefined,
              },
            },
          }}
        />
      </div>
    </LocalizationProvider>
  );
};

DateInputBlock.craft = {
  displayName: "Campo de fecha",
  props: {
    label: "Etiqueta",
    value: null,
    variant: "outlined",
    fullWidth: true,
    size: "medium",
    helperText: "",
    disablePast: false,
    availability: {
      weekDays: [],
      blockedDates: [],
    },
    required: false,
    icon: "",
    iconLocation: "start",
    iconStroke: 2,
    iconSize: 20,
    iconColor: "#000000",
  },
  related: {
    settings: DateInputSettings,
  },
  custom: {
    formField: true,
  },
};

export default DateInputBlock;
